import { useMutation } from '@tanstack/react-query';
import { findIndex, remove } from 'lodash';
import { produce } from 'immer';
import * as guidelinesApi from '../../helpers/api/guidelinesApi';
import { GUIDE_KEYS } from '../../queries/guides';
import {
  AudioWidget,
  ButtonWidget,
  CardWidget,
  ColorPaletteWidget,
  FigmaWidget,
  ImageWidget,
  Page,
  SpacerWidget,
  TeaWidget,
  TextWidget,
  VideoWidget,
  WIDGET_TYPE,
  YoutubeWidget,
} from '../../types';
import { handleMutationSuccess, updateGuideVersion } from '../helpers';
import { queryClient } from '../../constants/app';
import { ImageActions } from '../../constants/widgets';
import { useGuideStore } from '../../stores/guideStore';
import { getNewWidgetByType } from '../../components/widgets/helpers';
import { addNewButtonWidgetToSection } from '../../helpers/api/widgets/button';
import { addNewCardWidgetToSectionApi } from '../../helpers/api/widgets/card';
import { addNewSpacerWidgetToSection } from '../../helpers/api/widgets/spacing';
import { addNewYoutubeWidgetToSectionApi } from '../../helpers/api/widgets/youtube';
import { addNewVimeoWidgetToSection } from '../../helpers/api/widgets/vimeo';
import { addNewColorPaletteWidgetToSectionApi } from '../../helpers/api/widgets/colorPalette';
import { addNewFigmaWidgetToSectionApi } from '../../helpers/api/widgets/figma';

export const useWidgetMutations = (
  sectionId?: string,
  columnIndex?: number,
  widgetId?: string,
) => {
  const activeGuideId = useGuideStore(s => s.activeGuideId);
  const activePageId = useGuideStore(s => s.activePageId);
  const activeChapterId = useGuideStore(s => s.activeChapterId);
  const activeGuideVersion = useGuideStore(s => s.activeGuideVersion);

  return {
    addWidget: useMutation(
      (options: {
        widgetType: WIDGET_TYPE;
        newWidgetId: string;
        performOptimisticUpdate?: boolean;
        widgetProps?: unknown;
      }) => {
        if (
          activeGuideId &&
          activeChapterId &&
          activePageId &&
          sectionId &&
          typeof columnIndex === 'number' &&
          activeGuideVersion
        ) {
          switch (options.widgetType) {
            case WIDGET_TYPE.TABLE: {
              return guidelinesApi.addNewTableWidgetToSection(
                activeGuideId,
                activeChapterId,
                activePageId,
                sectionId,
                options.newWidgetId,
                options.widgetType,
                columnIndex,
                activeGuideVersion,
              );
            }
            case WIDGET_TYPE.TEXT:
              return guidelinesApi.addNewTextWidgetToSection(
                activeGuideId,
                activeChapterId,
                activePageId,
                sectionId,
                options.newWidgetId,
                options.widgetType,
                columnIndex,
                activeGuideVersion,
              );

            case WIDGET_TYPE.TEA:
            return guidelinesApi.addNewTeaWidgetToSection(
              activeGuideId,
              activeChapterId,
              activePageId,
              sectionId,
              options.newWidgetId,
              columnIndex,
              activeGuideVersion,
            );

          case WIDGET_TYPE.IMAGE:
            return guidelinesApi.addNewImageWidgetToSection(
              activeGuideId,
              activeChapterId,
              activePageId,
              sectionId,
              options.newWidgetId,
              options.widgetType,
              columnIndex,
              activeGuideVersion,
            );
          case WIDGET_TYPE.COLOR_PALETTE:
            return addNewColorPaletteWidgetToSectionApi(
              activeGuideId,
              activeChapterId,
              activePageId,
              sectionId,
              options.newWidgetId,
              options.widgetType,
              columnIndex,
              activeGuideVersion,
            );
          case WIDGET_TYPE.VIDEO:
            return guidelinesApi.addNewVideoWidgetToSection(
              activeGuideId,
              activeChapterId,
              activePageId,
              sectionId,
              options.newWidgetId,
              options.widgetType,
              columnIndex,
              activeGuideVersion,
            );
          case WIDGET_TYPE.AUDIO:
            return guidelinesApi.addNewAudioWidgetToSection(
              activeGuideId,
              activeChapterId,
              activePageId,
              sectionId,
              options.newWidgetId,
              options.widgetType,
              columnIndex,
              activeGuideVersion,
            );
          case WIDGET_TYPE.SPACER:
            return addNewSpacerWidgetToSection(
              activeGuideId,
              activeChapterId,
              activePageId,
              sectionId,
              options.newWidgetId,
              options.widgetType,
              columnIndex,
              activeGuideVersion,
            );
          case WIDGET_TYPE.CARD:
            return addNewCardWidgetToSectionApi({
              guideId: activeGuideId,
              chapterId: activeChapterId,
              pageId: activePageId,
              sectionId,
              widgetId: options.newWidgetId,
              columnIndex,
              guideVersion: activeGuideVersion,
              cardId: (options.widgetProps as { cardId: string })?.cardId,
            });
          case WIDGET_TYPE.BUTTON:
            return addNewButtonWidgetToSection({
              guideId: activeGuideId,
              chapterId: activeChapterId,
              pageId: activePageId,
              sectionId,
              widgetId: options.newWidgetId,
              columnIndex,
              guideVersion: activeGuideVersion,
              buttonId: (options.widgetProps as { buttonId: string })?.buttonId,
            });
          case WIDGET_TYPE.YOUTUBE:
            return addNewYoutubeWidgetToSectionApi({
              guideId: activeGuideId,
              chapterId: activeChapterId,
              pageId: activePageId,
              sectionId,
              widgetId: options.newWidgetId,
              columnIndex,
              guideVersion: activeGuideVersion,
              youtubeWidgetPayload: {
                // sourceId: '',
                settings: {
                  loop: false,
                  autoplay: false,
                  controls: true,
                },
              },
            });
          case WIDGET_TYPE.VIMEO:
            return addNewVimeoWidgetToSection({
              guideId: activeGuideId,
              chapterId: activeChapterId,
              pageId: activePageId,
              sectionId,
              widgetId: options.newWidgetId,
              columnIndex,
              guideVersion: activeGuideVersion,
              sourceId: (options.widgetProps as { sourceId: string })?.sourceId,
            });
          case WIDGET_TYPE.FIGMA:
            return addNewFigmaWidgetToSectionApi({
              guideId: activeGuideId,
              chapterId: activeChapterId,
              pageId: activePageId,
              sectionId,
              widgetId: options.newWidgetId,
              columnIndex,
              guideVersion: activeGuideVersion,
              figmaWidgetPayload: {
                // sourceId: '',
                settings: {
                  theme: 'light',
                  footer: true,
                  controls: true,
                },
              },
            });
          default:
            break;}
        }
        return Promise.reject('Missing required fields');
      },
      {
        onMutate: variables => {
          updateGuideVersion(activeGuideId!);
          if (variables.performOptimisticUpdate) {
            optimisticallyAddWidgetInSection(
              activeGuideId!,
              activePageId!,
              sectionId!,
              columnIndex!,
              variables.newWidgetId,
              variables.widgetType,
              variables.widgetProps,
            );
          }
        },
        onSuccess: (data, variables) => {
          let trackingMessage: string;
          switch (variables.widgetType) {
            case WIDGET_TYPE.TEXT:
              trackingMessage = 'Text Widget Created';
              break;
            case WIDGET_TYPE.IMAGE:
              trackingMessage = 'Image Widget Created';
              break;
            default:
              trackingMessage = 'Widget Added';
          }
          handleMutationSuccess(trackingMessage, data, {
            entityId: activeGuideId!,
          });
        },
      },
    ),
    deleteWidget: useMutation(
      () => {
        if (
          activeGuideId &&
          activeChapterId &&
          activePageId &&
          sectionId &&
          widgetId &&
          activeGuideVersion
        ) {
          return guidelinesApi.deleteWidgetFromSection(
            activeGuideId,
            activeChapterId,
            activePageId,
            sectionId,
            widgetId,
            activeGuideVersion,
          );
        }
        return Promise.reject('Missing required fields');
      },
      {
        onMutate: () => {
          updateGuideVersion(activeGuideId!);
          optimisticallyDeleteWidget(
            activeGuideId!,
            activePageId!,
            sectionId!,
            columnIndex!,
            widgetId!,
          );
        },
        onSuccess: data => {
          handleMutationSuccess('Widget Deleted', data, {
            entityId: activeGuideId!,
          });
        },
      },
    ),
    moveWidget: useMutation(
      (options: {
        fromSectionId: string;
        fromColumnIndex: number;
        widgetId: string;
        toSectionId: string;
        toColumnIndex: number;
        toPosition: number;
      }) => {
        if (
          activeGuideId &&
          activeChapterId &&
          activePageId &&
          activeGuideVersion
        ) {
          return guidelinesApi.moveWidget(
            activeGuideId,
            activeChapterId,
            activePageId,
            options.fromSectionId,
            options.widgetId,
            options.fromColumnIndex,
            {
              sectionId: options.toSectionId,
              columnIndex: options.toColumnIndex,
              position: options.toPosition,
            },
            activeGuideVersion,
          );
        }
        return Promise.reject('Missing required fields');
      },

      {
        onMutate: variables => {
          updateGuideVersion(activeGuideId!);
          optimisticallyMoveWidget(activeGuideId!, activePageId!, variables);
        },
        onSuccess: data => {
          handleMutationSuccess('Widget Moved', data, {
            entityId: activeGuideId!,
          });
        },
      },
    ),
  };
};

const optimisticallyAddWidgetInSection = (
  guideId: string,
  pageId: string,
  sectionId: string,
  columnIndex: number,
  widgetId: string,
  widgetType: WIDGET_TYPE,
  widgetProps?: unknown,
) => {
  queryClient.setQueryData(
    [GUIDE_KEYS.PAGE_BY_ID, guideId, pageId],
    (old: Page) => {
      const sectionIndex = findIndex(
        old.sections,
        section => section.id === sectionId,
      );

      if (old) {
        return produce(old, draft => {
          draft.sections[sectionIndex].columns[columnIndex].widgets.push(
            getFreshWidget(widgetId, widgetType, widgetProps),
          );
        });
      }

      return old;
    },
  );
};

const optimisticallyMoveWidget = (
  guideId: string,
  pageId: string,

  variables: {
    fromSectionId: string;
    fromColumnIndex: number;
    widgetId: string;
    toSectionId: string;
    toColumnIndex: number;
    toPosition: number;
  },
) => {
  queryClient.setQueryData(
    [GUIDE_KEYS.PAGE_BY_ID, guideId, pageId],
    (old: Page) => {
      if (old) {
        const fromSectionIndex = findIndex(
          old.sections,
          section => section.id === variables.fromSectionId,
        );
        const widgetIndex = findIndex(
          old.sections[fromSectionIndex].columns[variables.fromColumnIndex]
            .widgets,
          widget => widget.id === variables.widgetId,
        );
        const toSectionIndex = findIndex(
          old.sections,
          section => section.id === variables.toSectionId,
        );

        return produce(old, draft => {
          const widget = {
            ...old.sections[fromSectionIndex].columns[variables.fromColumnIndex]
              .widgets[widgetIndex],
          };
          draft.sections[fromSectionIndex].columns[
            variables.fromColumnIndex
          ].widgets.splice(widgetIndex, 1);

          draft.sections[toSectionIndex].columns[
            variables.toColumnIndex
          ].widgets.splice(variables.toPosition, 0, widget);
        });
      }

      return old;
    },
  );
};

const optimisticallyDeleteWidget = (
  guideId: string,
  pageId: string,
  sectionId: string,
  columnIndex: number,
  widgetId: string,
) => {
  queryClient.setQueryData(
    [GUIDE_KEYS.PAGE_BY_ID, guideId, pageId],
    (old: Page) => {
      const sectionIndex = findIndex(
        old.sections,
        section => section.id === sectionId,
      );

      if (old) {
        return produce(old, draft => {
          remove(
            draft.sections[sectionIndex].columns[columnIndex].widgets,
            widget => widget.id === widgetId,
          );
        });
      }

      return old;
    },
  );
};

const getFreshWidget = (
  widgetId: string,
  widgetType: WIDGET_TYPE,
  widgetProps?: unknown,
) => {
  switch (widgetType) {
    case WIDGET_TYPE.FIGMA:
      return {
        id: widgetId,
        type: widgetType,
        ...getNewWidgetByType(WIDGET_TYPE.FIGMA, widgetProps),
      } as FigmaWidget;
    case WIDGET_TYPE.YOUTUBE:
      return {
        id: widgetId,
        type: widgetType,
        ...getNewWidgetByType(WIDGET_TYPE.YOUTUBE, widgetProps),
      } as YoutubeWidget;
    case WIDGET_TYPE.BUTTON:
      return {
        id: widgetId,
        type: widgetType,
        ...getNewWidgetByType(WIDGET_TYPE.BUTTON, widgetProps),
      } as ButtonWidget;
    case WIDGET_TYPE.CARD:
      return {
        id: widgetId,
        type: widgetType,
        ...getNewWidgetByType(WIDGET_TYPE.CARD, widgetProps),
      } as CardWidget;
    case WIDGET_TYPE.TEXT:
      return {
        id: widgetId,
        type: widgetType,
      } as TextWidget;
      case WIDGET_TYPE.TEA:
        return {
          id: widgetId,
          type: widgetType,
        } as TeaWidget;

    case WIDGET_TYPE.VIDEO:
      return {
        id: widgetId,
        type: widgetType,
        controls: true,
        loop: false,
        autoplay: false,
        muted: false,
      } as VideoWidget;

    case WIDGET_TYPE.AUDIO:
      return {
        id: widgetId,
        type: widgetType,
      } as AudioWidget;

    case WIDGET_TYPE.SPACER:
      return {
        id: widgetId,
        type: widgetType,
        ...getNewWidgetByType(WIDGET_TYPE.SPACER),
      } as SpacerWidget;

    case WIDGET_TYPE.IMAGE:
      return {
        id: widgetId,
        type: widgetType,
        imageAction: { type: ImageActions.OPEN_PREVIEW, openNewTab: false },
      } as ImageWidget;

    case WIDGET_TYPE.COLOR_PALETTE:
      return {
        id: widgetId,
        type: widgetType,
        defaultColorCodes: {},
        colors: [],
        style: {
          stylesheet: '',
          language: 'css',
          version: 3,
        },
      } as ColorPaletteWidget;

    case WIDGET_TYPE.TABLE: {
      const defaultRows = 3;
      const defaultColumns = 3;

      return {
        id: widgetId,
        type: widgetType,
        table: {
          rows: Array.from({ length: defaultRows }, () => ({
            cells: Array.from({ length: defaultColumns }, () => ({
              content: null,
              properties: {},
            })),
          })),
          properties: {
            numRows: defaultRows,
            numColumns: defaultColumns,
          },
        },
      };
    }
    case WIDGET_TYPE.VIMEO:
      return {
        id: widgetId,
        type: widgetType,
        ...getNewWidgetByType(WIDGET_TYPE.VIMEO),
      };

    default:
      // Return a basic widget to ensure we never return undefined
      return {
        id: widgetId,
        type: widgetType,
      };
  }
};
